import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, User, ArrowLeft, Zap
} from 'lucide-react';

// Night Mode Theme - Dark, neon, cyber-inspired for gamers
// Colors: Near Black background (#09090B), Magenta primary (#D946EF), Purple (#8B5CF6), Neon Green (#22C55E)
// Font: Rajdhani (headings), JetBrains Mono (body)
// Style: Sharp corners, glowing shadows, neon accents

const NightModePreview = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: '#09090B',
      fontFamily: "'JetBrains Mono', monospace",
      color: '#FAFAFA'
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-zinc-900 border border-zinc-700 rounded p-2 hover:border-fuchsia-500 transition-colors"
        style={{ boxShadow: '0 0 10px rgba(217, 70, 239, 0.3)' }}
      >
        <ArrowLeft className="w-5 h-5 text-fuchsia-400" />
      </button>

      {/* Theme Label */}
      <div 
        className="fixed top-4 right-4 z-50 bg-zinc-900 text-fuchsia-400 px-4 py-2 rounded font-bold text-sm border border-fuchsia-500"
        style={{ boxShadow: '0 0 15px rgba(217, 70, 239, 0.4)' }}
      >
        Theme 3: Night Mode
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden border-b border-zinc-800">
        {/* Grid background effect */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(217, 70, 239, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(217, 70, 239, 0.3) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Glow effects */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(217, 70, 239, 0.15)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }} />
        
        <div className="relative px-4 pt-16 pb-8">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded bg-zinc-900 border border-fuchsia-500 flex items-center justify-center"
                  style={{ boxShadow: '0 0 10px rgba(217, 70, 239, 0.5)' }}
                >
                  <Zap className="w-5 h-5 text-fuchsia-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Newark::SYEP</p>
                  <p className="text-sm text-fuchsia-400 font-bold">v2026.summer</p>
                </div>
              </div>
              <button className="text-zinc-500 hover:text-fuchsia-400 p-2 rounded border border-zinc-800 hover:border-fuchsia-500/50 transition-all">
                <LogOut className="w-5 h-5" />
              </button>
            </div>

            {/* Welcome Section */}
            <div className="flex items-center gap-4 mb-6">
              <div 
                className="w-16 h-16 rounded bg-gradient-to-br from-fuchsia-600 to-violet-600 flex items-center justify-center border border-fuchsia-400"
                style={{ boxShadow: '0 0 20px rgba(217, 70, 239, 0.4)' }}
              >
                <span className="text-2xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>B</span>
              </div>
              <div>
                <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-1">player_status: active</p>
                <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  BRANDON_WILLIAMS
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-green-400 font-mono">● online</span>
                  <span className="text-xs text-zinc-600">|</span>
                  <span className="bg-violet-500/20 text-violet-400 text-xs font-mono px-2 py-0.5 rounded border border-violet-500/30">
                    UPNEXT
                  </span>
                </div>
              </div>
            </div>

            {/* Worksite Info */}
            <div className="bg-zinc-900/80 border border-zinc-800 rounded p-4 mb-6">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-green-400" />
                <div>
                  <p className="text-xs text-zinc-600 font-mono uppercase">location_assigned:</p>
                  <p className="text-sm text-zinc-300 font-mono">Newark Public Library</p>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Points Card */}
              <div 
                className="bg-zinc-900 border border-fuchsia-500/50 rounded p-5"
                style={{ boxShadow: '0 0 15px rgba(217, 70, 239, 0.2), inset 0 0 30px rgba(217, 70, 239, 0.05)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-fuchsia-400" />
                  <span className="text-xs font-mono text-zinc-500 uppercase">xp_total</span>
                </div>
                <p className="text-3xl font-bold text-fuchsia-400" style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 20px rgba(217, 70, 239, 0.5)' }}>
                  1,250
                </p>
              </div>

              {/* Streak Card */}
              <div 
                className="bg-zinc-900 border border-green-500/50 rounded p-5"
                style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.2), inset 0 0 30px rgba(34, 197, 94, 0.05)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-4 h-4 text-green-400" />
                  <span className="text-xs font-mono text-zinc-500 uppercase">streak_days</span>
                </div>
                <p className="text-3xl font-bold text-green-400" style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>
                  7 <span className="text-lg text-zinc-600">d</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-8 space-y-8">
        
        {/* Quick Actions */}
        <div>
          <h2 className="text-xs font-mono text-zinc-600 uppercase tracking-wider mb-4">// quick_actions</h2>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Target, label: 'GOALS', color: 'text-green-400 border-green-500/30' },
              { icon: Briefcase, label: 'PROJECT', color: 'text-orange-400 border-orange-500/30' },
              { icon: Trophy, label: 'RANKS', color: 'text-yellow-400 border-yellow-500/30' },
              { icon: Award, label: 'WINS', color: 'text-pink-400 border-pink-500/30' },
              { icon: FolderOpen, label: 'FILES', color: 'text-amber-400 border-amber-500/30' },
              { icon: HelpCircle, label: 'HELP', color: 'text-violet-400 border-violet-500/30' },
            ].map((item) => (
              <button
                key={item.label}
                className={`bg-zinc-900 border ${item.color} rounded p-4 hover:bg-zinc-800 transition-colors`}
              >
                <item.icon className={`w-5 h-5 mx-auto mb-2 ${item.color.split(' ')[0]}`} />
                <p className="text-xs font-mono text-center">{item.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Work Journal Card */}
        <div 
          className="bg-zinc-900 border border-fuchsia-500 rounded p-6 relative overflow-hidden"
          style={{ boxShadow: '0 0 30px rgba(217, 70, 239, 0.3)' }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(217, 70, 239, 0.2)' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-fuchsia-500/20 rounded flex items-center justify-center border border-fuchsia-500/50">
                <Sparkles className="w-5 h-5 text-fuchsia-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>WORK_JOURNAL</h2>
                <p className="text-zinc-500 text-xs font-mono">submit weekly progress</p>
              </div>
            </div>
            <button 
              className="w-full bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white rounded py-3.5 font-bold hover:from-fuchsia-500 hover:to-violet-500 transition-all border border-fuchsia-400"
              style={{ fontFamily: "'Rajdhani', sans-serif", boxShadow: '0 0 20px rgba(217, 70, 239, 0.4)' }}
            >
              DROP_WIN (+50 XP)
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-zinc-600" />
            <h2 className="text-xs font-mono text-zinc-600 uppercase tracking-wider">// weekly_quests</h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div 
              className="bg-gradient-to-r from-violet-900/50 to-fuchsia-900/50 border border-violet-500/50 rounded p-4 cursor-pointer hover:border-violet-400 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-violet-500/20 flex items-center justify-center border border-violet-500/30">
                    <Brain className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>SKILLS_SCAN</h3>
                    <p className="text-zinc-500 text-xs font-mono">+50 xp • unlock achievements</p>
                  </div>
                </div>
                <span className="bg-green-500/20 text-green-400 text-xs font-mono px-2 py-1 rounded border border-green-500/30 animate-pulse">
                  NEW
                </span>
              </div>
            </div>

            {/* Check-in Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded p-4 hover:border-zinc-700 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-300 font-mono">check_in</h3>
                    <p className="text-zinc-600 text-xs font-mono">+20 xp</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-700" />
              </div>
            </div>

            {/* Trivia Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded p-4 hover:border-zinc-700 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-300 font-mono">trivia_challenge</h3>
                    <p className="text-zinc-600 text-xs font-mono">+25 xp</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div 
          className="bg-zinc-900 border border-violet-500/50 rounded p-5"
          style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500/20 rounded flex items-center justify-center border border-violet-500/30">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>TEAM_ALPHA</h3>
                <p className="text-zinc-600 text-xs font-mono">8 players online</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-violet-400" style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 15px rgba(139, 92, 246, 0.5)' }}>4,500</p>
              <p className="text-zinc-600 text-xs font-mono">team_xp</p>
            </div>
          </div>
          <button className="w-full bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/50 rounded py-3 text-violet-400 font-mono transition-colors">
            view_team &gt;&gt;
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 px-4 py-2">
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: '🏠', label: 'HOME', active: true, color: 'text-fuchsia-400' },
            { icon: '🎯', label: 'GOALS', active: false, color: 'text-zinc-600' },
            { icon: '📊', label: 'STATS', active: false, color: 'text-zinc-600' },
            { icon: '👤', label: 'PROFILE', active: false, color: 'text-zinc-600' },
          ].map((item) => (
            <button 
              key={item.label} 
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded transition-all ${
                item.active 
                  ? 'text-fuchsia-400' 
                  : 'text-zinc-600 hover:text-zinc-400'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-xs font-mono">{item.label}</span>
              {item.active && (
                <div className="w-4 h-0.5 bg-fuchsia-400 rounded mt-1" style={{ boxShadow: '0 0 10px rgba(217, 70, 239, 0.8)' }} />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NightModePreview;
